/**
 * OneDrive and SharePoint through Microsoft Graph, called from the browser
 * as the signed-in user.
 *
 * The file itself never passes through Graph's JSON API: downloads use the
 * short-lived pre-authenticated URL Graph hands out, and large uploads go to
 * an upload session in chunks. Both live on *.sharepoint.com, which the
 * Content-Security-Policy must allow (deploy/apache/odie.conf).
 *
 * Saving back is conditional on the file's eTag, so a save never silently
 * overwrites someone else's changes made since it was opened.
 */

import { graphToken } from '../auth/msal'

const GRAPH = 'https://graph.microsoft.com/v1.0'
/** Graph's limit for a simple PUT; above it, an upload session. */
const SIMPLE_UPLOAD_MAX = 4 * 1024 * 1024
/** Upload-session chunks must be multiples of 320 KiB. */
const CHUNK = 320 * 1024 * 16

export interface DriveItem {
  id: string
  driveId: string
  name: string
  folder: boolean
  size: number
  modified: string
  eTag: string
  isPdf: boolean
  /** Who changed it last, for the list. */
  modifiedBy: string
}

export interface DriveLocation {
  driveId: string
  /** A folder's item id, or 'root'. */
  itemId: string
  name: string
}

export interface Site {
  id: string
  name: string
  url: string
}

export class GraphError extends Error {
  constructor(readonly status: number, message: string) {
    super(message)
  }
}

/** The file was changed by someone else since it was opened. */
export class ChangedSinceOpened extends GraphError {}

async function call(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await graphToken()
  const headers = new Headers(init.headers)
  headers.set('Authorization', `Bearer ${token}`)
  const response = await fetch(path.startsWith('http') ? path : GRAPH + path, { ...init, headers })
  if (response.status === 412) {
    throw new ChangedSinceOpened(412, 'Someone else changed this file since you opened it.')
  }
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`
    try {
      const body = await response.json()
      message = body?.error?.message ?? message
    } catch {
      // not JSON
    }
    if (response.status === 403) message = `You do not have access to that. (${message})`
    throw new GraphError(response.status, message)
  }
  return response
}

async function list(path: string, limit = 1000): Promise<Record<string, any>[]> {
  const out: Record<string, any>[] = []
  let next: string | null = path
  while (next && out.length < limit) {
    const body: Record<string, any> = await (await call(next)).json()
    out.push(...(body.value ?? []))
    next = body['@odata.nextLink'] ?? null
  }
  return out
}

function toItem(raw: Record<string, any>): DriveItem {
  // Shared-with-me and recent items describe the real file under remoteItem.
  const item = raw.remoteItem ?? raw
  const name: string = item.name ?? raw.name ?? ''
  return {
    id: item.id,
    driveId: item.parentReference?.driveId ?? raw.parentReference?.driveId ?? '',
    name,
    folder: Boolean(item.folder ?? raw.folder),
    size: item.size ?? raw.size ?? 0,
    modified: item.lastModifiedDateTime ?? raw.lastModifiedDateTime ?? '',
    eTag: item.eTag ?? raw.eTag ?? '',
    isPdf: /\.pdf$/i.test(name) || (item.file?.mimeType ?? raw.file?.mimeType) === 'application/pdf',
    modifiedBy: item.lastModifiedBy?.user?.displayName ?? raw.lastModifiedBy?.user?.displayName ?? '',
  }
}

const sortItems = (items: DriveItem[]) =>
  items.sort((a, b) => (a.folder === b.folder ? a.name.localeCompare(b.name) : a.folder ? -1 : 1))

const children = (location: DriveLocation) =>
  location.itemId === 'root'
    ? `/drives/${location.driveId}/root/children?$top=200`
    : `/drives/${location.driveId}/items/${location.itemId}/children?$top=200`

export const oneDrive = {
  convertToPdf: (file: File) => convertToPdf(file),

  /** An Office file already in OneDrive or SharePoint, as PDF: Microsoft 365
   * converts it where it is, so nothing is uploaded and nothing left behind. */
  async officeAsPdf(item: DriveItem): Promise<Blob> {
    try {
      const response = await call(`/drives/${item.driveId}/items/${item.id}/content?format=pdf`)
      const pdf = await response.blob()
      if (!pdf.size) throw new GraphError(502, 'Microsoft 365 returned an empty PDF.')
      return new Blob([pdf], { type: 'application/pdf' })
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err)
      throw new GraphError(err instanceof GraphError ? err.status : 0,
        `Microsoft 365 could not convert ${item.name}: ${reason}`)
    }
  },

  /** The user's own OneDrive. */
  async myDrive(): Promise<DriveLocation> {
    const drive = await (await call('/me/drive?$select=id,name')).json()
    return { driveId: drive.id, itemId: 'root', name: 'My files' }
  },

  async folder(location: DriveLocation): Promise<DriveItem[]> {
    return sortItems((await list(children(location))).map(toItem))
  },

  async recent(): Promise<DriveItem[]> {
    return (await list('/me/drive/recent?$top=100', 100)).map(toItem).filter((i) => !i.folder)
  },

  async shared(): Promise<DriveItem[]> {
    return sortItems((await list('/me/drive/sharedWithMe?$top=200')).map(toItem))
  },

  async search(location: DriveLocation, query: string): Promise<DriveItem[]> {
    const q = encodeURIComponent(query.replace(/'/g, "''"))
    return sortItems((await list(`/drives/${location.driveId}/root/search(q='${q}')?$top=200`, 200)).map(toItem))
  },

  /** SharePoint sites: the ones the user follows, or a search. */
  async sites(query = ''): Promise<Site[]> {
    const path = query.trim()
      ? `/sites?search=${encodeURIComponent(query.trim())}&$top=50`
      : '/me/followedSites?$top=100'
    return (await list(path, 100)).map((s) => ({ id: s.id, name: s.displayName ?? s.name, url: s.webUrl }))
  },

  /** A site's document libraries. */
  async libraries(site: Site): Promise<DriveLocation[]> {
    return (await list(`/sites/${site.id}/drives?$select=id,name`)).map((d) => ({
      driveId: d.id, itemId: 'root', name: d.name,
    }))
  },

  async download(item: DriveItem): Promise<{ blob: Blob; item: DriveItem }> {
    const meta = await (await call(
      `/drives/${item.driveId}/items/${item.id}?$select=id,name,eTag,size,parentReference,file,lastModifiedDateTime,@microsoft.graph.downloadUrl`,
    )).json()
    const url = meta['@microsoft.graph.downloadUrl']
    if (!url) throw new GraphError(404, 'OneDrive did not offer a download for this file.')
    const response = await fetch(url)            // pre-authenticated: no token
    if (!response.ok) throw new GraphError(response.status, `Download failed (${response.status}).`)
    return { blob: await response.blob(), item: toItem(meta) }
  },

  /**
   * Save over ``item``. With ``eTag``, only if nobody changed it since:
   * otherwise ChangedSinceOpened. Returns the item as saved (new eTag).
   */
  async replace(item: { id: string; driveId: string }, blob: Blob, eTag?: string): Promise<DriveItem> {
    const headers: Record<string, string> = { 'Content-Type': 'application/pdf' }
    if (eTag) headers['If-Match'] = eTag
    if (blob.size <= SIMPLE_UPLOAD_MAX) {
      const response = await call(`/drives/${item.driveId}/items/${item.id}/content`, {
        method: 'PUT', headers, body: blob,
      })
      return toItem(await response.json())
    }
    const session = await (await call(`/drives/${item.driveId}/items/${item.id}/createUploadSession`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(eTag ? { 'If-Match': eTag } : {}) },
      body: JSON.stringify({ item: { '@microsoft.graph.conflictBehavior': 'replace' } }),
    })).json()
    return chunked(session.uploadUrl, blob)
  },

  /** Save as a new file in ``folder``; refuses to overwrite unless ``overwrite``. */
  async create(folder: DriveLocation, name: string, blob: Blob, overwrite = false): Promise<DriveItem> {
    const behaviour = overwrite ? 'replace' : 'fail'
    const target = folder.itemId === 'root'
      ? `/drives/${folder.driveId}/root:/${encodeURIComponent(name)}:`
      : `/drives/${folder.driveId}/items/${folder.itemId}:/${encodeURIComponent(name)}:`
    if (blob.size <= SIMPLE_UPLOAD_MAX) {
      const response = await call(`${target}/content?@microsoft.graph.conflictBehavior=${behaviour}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/pdf' }, body: blob,
      })
      return toItem(await response.json())
    }
    const session = await (await call(`${target}/createUploadSession`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ item: { '@microsoft.graph.conflictBehavior': behaviour } }),
    })).json()
    return chunked(session.uploadUrl, blob)
  },
}

/** Where Office files wait, for a moment, to be converted. */
const CONVERSIONS = '/me/drive/root:/Apps/Odie conversions'

/**
 * An Office document as PDF, converted by Microsoft 365. The file is put in
 * the user's OneDrive under Apps/Odie conversions, fetched back as PDF, and
 * deleted -- whether the conversion worked or not.
 */
export async function convertToPdf(file: File): Promise<Blob> {
  const name = `${Date.now().toString(36)}-${file.name.replace(/[\\/:*?"<>|#%]/g, '_')}`
  const target = `${CONVERSIONS}/${encodeURIComponent(name)}:`
  let item: DriveItem
  if (file.size <= SIMPLE_UPLOAD_MAX) {
    const response = await call(`${target}/content?@microsoft.graph.conflictBehavior=rename`, {
      method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: file,
    })
    item = toItem(await response.json())
  } else {
    const session = await (await call(`${target}/createUploadSession`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ item: { '@microsoft.graph.conflictBehavior': 'rename' } }),
    })).json()
    item = await chunked(session.uploadUrl, file)
  }
  try {
    // Graph answers with a redirect to Microsoft's converter, which the
    // browser follows (and CSP allows: *.svc.ms).
    const response = await call(`/drives/${item.driveId}/items/${item.id}/content?format=pdf`)
    const pdf = await response.blob()
    if (!pdf.size) throw new GraphError(502, `Microsoft 365 returned an empty PDF for ${file.name}.`)
    return new Blob([pdf], { type: 'application/pdf' })
  } catch (err) {
    if (err instanceof GraphError) {
      throw new GraphError(err.status, `Microsoft 365 could not convert ${file.name}: ${err.message}`)
    }
    throw new GraphError(0, `Microsoft 365 could not convert ${file.name}. ${err instanceof Error ? err.message : ''}`)
  } finally {
    void call(`/drives/${item.driveId}/items/${item.id}`, { method: 'DELETE' }).catch(() => undefined)
  }
}

async function chunked(uploadUrl: string, blob: Blob): Promise<DriveItem> {
  let offset = 0
  while (offset < blob.size) {
    const end = Math.min(offset + CHUNK, blob.size)
    // The upload URL is pre-authenticated; sending a token to it is refused.
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Range': `bytes ${offset}-${end - 1}/${blob.size}` },
      body: blob.slice(offset, end),
    })
    if (response.status === 412) throw new ChangedSinceOpened(412, 'Someone else changed this file since you opened it.')
    if (!response.ok) throw new GraphError(response.status, `Upload failed (${response.status}).`)
    if (response.status === 200 || response.status === 201) return toItem(await response.json())
    offset = end
  }
  throw new GraphError(500, 'Upload did not complete.')
}
